import { useEffect, useRef } from 'react';

/**
 * Returns an AbortSignal that is automatically aborted when the calling
 * component unmounts. Pass the signal to action creators that support it
 * to cancel in-flight API requests when the user navigates away.
 *
 * Usage:
 *   const signal = useAbortSignal();
 *   dispatch(getHaldorAssignments(signal, graphId));
 *
 * @returns {AbortSignal}
 */
const useAbortSignal = () => {
	const controllerRef = useRef(null);

	// Create the controller once on mount — useRef ensures it is stable
	// across re-renders without triggering new effect runs.
	if (controllerRef.current === null) {
		controllerRef.current = new AbortController();
	}

	useEffect(() => {
		// Abort any in-flight requests when the component unmounts.
		return () => {
			console.log('[useAbortSignal] Aborting in-flight requests on unmount');
			controllerRef.current.abort();
		};
	}, []);

	return controllerRef.current.signal;
};

export default useAbortSignal;
